import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ImageDown, RefreshCw } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createExportJob, getExportJob } from "../../api/CanvaApi";
import { urlToFile } from "../helpers";
import LayoutAction from "../../actions/LayoutAction";

export interface CanvaDesignPageProps {
  designId: string;
  designTitle?: string;
  item: any;
  onExportImage: (filename: string, file: File, meta?: { width?: number; height?: number }) => Promise<void>;
  className?: string;
}

const CanvaDesignPage = ({ designId, designTitle, item, onExportImage, className }: CanvaDesignPageProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [exportId, setExportId] = useState("");
  const [isLoadingExportJob, setIsLoadingExportJob] = useState(false);

  const createExportJobMutation = useMutation({
    mutationFn: async ({ designId, page }: any) => {
      // const exportFormats = await getExportFormats(designId);
      // console.log(exportFormats);
      return await createExportJob(designId, page);
    },
    onSuccess: (data) => {
      setExportId(data.job.id);
    },
  });

  useQuery({
    queryKey: ["canva-export", exportId],
    queryFn: () => getExportJob(exportId),
    enabled: !!exportId,
    refetchInterval: ({ state }) => {
      const status = state.data?.job?.status;
      if (status === "success") {
        setExportId("");
        const urls = state.data?.job?.urls || [];
        if (urls.length > 0) {
          // Build a meaningful filename: "Design Title - Page N"
          const pageNum = item.get("index") || 1;
          const preferredName = designTitle
            ? `${designTitle} - Page ${pageNum}`
            : undefined;
          urlToFile(urls[0], preferredName).then(async ({ file, filename }) => {
            if (file && filename) {
              const dimWidth = item.getIn(["dimensions", "width"]);
              const dimHeight = item.getIn(["dimensions", "height"]);
              const meta = (dimWidth && dimHeight)
                ? { width: dimWidth, height: dimHeight }
                : undefined;
              await onExportImage(filename, file, meta);
              setIsLoadingExportJob(false);
            }
          });
        }
        return false;
      }
      if (status === "failed") {
        dispatch(
          LayoutAction.setNotification({
            title: t("Export Image"),
            type: "error",
            message: t("Failed to export the image."),
          })
        );
        setIsLoadingExportJob(false);
        return false;
      }
      return 2000; // poll every 2s
    },
  });

  const pageIndex = item.get("index");
  const thumbnailUrl = item.getIn(["thumbnail", "url"]);
  const dimWidth = item.getIn(["dimensions", "width"]);
  const dimHeight = item.getIn(["dimensions", "height"]);
  const dimLabel = dimWidth && dimHeight
    ? `${Math.round(dimWidth)}×${Math.round(dimHeight)}`
    : null;

  return (
    <div className={`${className} h-full flex flex-col rounded-xl border border-gray-200 overflow-hidden bg-white`}>
      {/* Thumbnail */}
      <div className={`relative group bg-gray-50 flex-shrink-0 overflow-hidden aspect-[4/5]`}>
        <img
          src={thumbnailUrl}
          alt={`Page ${pageIndex}`}
          className="w-full h-full object-cover transition-opacity group-hover:opacity-75"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          {isLoadingExportJob ? (
            <div className="p-2 bg-[#7d2ae8] rounded-full shadow-lg">
              <RefreshCw className="w-5 h-5 text-white animate-spin" />
            </div>
          ) : (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7d2ae8] hover:bg-[#6b21cc] text-white text-xs font-semibold rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 cursor-pointer"
              onClick={() => {
                setIsLoadingExportJob(true);
                createExportJobMutation.mutate({ designId, page: pageIndex });
              }}
            >
              <ImageDown className="w-4 h-4" />
              {t("Export")}
            </div>
          )}
        </div>
        <div className="absolute top-1 left-1 bg-black/50 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
          {t("Page")} {pageIndex}
        </div>
      </div>

      {/* Info footer */}
      <div className="p-1 border-t border-gray-100 bg-white">
        {designTitle && (
          <p className="text-[11px] font-medium text-gray-700 truncate" title={designTitle}>
            {designTitle}
          </p>
        )}
        {dimLabel && (
          <p className="text-[10px] text-gray-400">{dimLabel} px</p>
        )}
      </div>
    </div>
  );
};

export default CanvaDesignPage;
